import type { ComponentType } from 'react'
import { CmsSectionContext, useCms } from './cms-context'
import { sectionStyle } from './style'
import type { CmsSection } from './types'

/* One section, wrapped in whatever the console has decided about it.
 *
 * The wrapper is a plain <div>, which is layout-neutral: the section's own
 * <section> keeps its id, so every anchor on the site — /home#cars, the
 * navbar's menus, a link somebody was sent — still lands where it always did.
 *
 * What the wrapper carries is three things. The custom properties that
 * recolour everything inside it, the data flags that switch on the layout
 * rules in index.css, and — only in the preview frame — the hooks the console
 * selects and outlines through.
 */

type SectionFrameProps = {
  section: CmsSection
  component: ComponentType
}

export function SectionFrame({ section, component: Component }: SectionFrameProps) {
  const { preview, selected } = useCms()

  /* A section switched off is not rendered at all — not hidden. Hiding it
     would leave its requests firing, its animations running and its images
     downloading for a band nobody can see. */
  if (!section.enabled) return null

  const { style, flags } = sectionStyle(section.style)

  const state =
    preview && selected.section === section.key && !selected.element ? 'selected' : undefined

  return (
    <div
      className="cms-section"
      style={style}
      data-cms-section={section.key}
      data-cms-state={state}
      {...flags}
    >
      <CmsSectionContext.Provider value={section}>
        <Component />
      </CmsSectionContext.Provider>
    </div>
  )
}
